/**
 * @architecture-header 1
 * @layer app
 * @domain parallel-development
 * @responsibility Let one lane-owned feature replace a Desktop/Mobile workspace content surface through a stable shared seam.
 * @state-owner route-selected workspace section and authenticated role projection
 * @allowed-imports React; app providers; shared workspace store; lane-owned content contributions
 * @forbidden feature-module internals; direct API clients; feature-specific behavior
 * @public-api shared workspace routes
 * @side-effects renders one lane-owned content override or the route fallback
 * @tests WorkspaceSectionContent.test.tsx and workspace Playwright flows
 */

import type { ReactNode } from 'react';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';
import { useActiveRole } from '../providers/ActiveRoleProvider';
import { agentASectionContent } from './agent-a/SectionContent';
import { agentBSectionContent } from './agent-b/SectionContent';
import type { WorkspaceContentSurface, WorkspaceSectionContentContext } from './types';

export function WorkspaceSectionContent({
  fallback,
  surface,
}: {
  readonly fallback: ReactNode;
  readonly surface: WorkspaceContentSurface;
}) {
  const role = useActiveRole();
  const section = useWorkspaceUiStore((state) => state.section);
  const context: WorkspaceSectionContentContext = { role, section, surface };
  const agentA = agentASectionContent(context, fallback);
  if (agentA !== undefined) return agentA;
  const agentB = agentBSectionContent(context, fallback);
  return agentB === undefined ? fallback : agentB;
}
